import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { AgentTransitionService, AgentTransitionRect, AgentTransitionState } from './agent-transition.service';

/**
 * App-root CARRIER overlay for the chat → editor transition.
 *
 * Sequence:
 *  1. A full-screen background carrier appears instantly — it hides the chat UI
 *     disappearing and the canvas that mounts on the editor route. It stays
 *     SOLID until the real canvas is found, then fades over ~500ms.
 *  2. When the canvas is found a preview carrier (full-screen host, initially
 *     clip-path masked to the source preview rect) runs a two-phase stretch:
 *       Phase 1 — left edge travels to canvas.x (rubber-band stretch).
 *       Phase 2 — right edge + vertical snap to canvas rect.
 *  3. Preview carrier dwells then fades out.
 *
 * CSS-transition correctness: each clip-path animation is split into two
 * consecutive timeouts so the browser has processed the `transition` property
 * before the target `clip-path` value changes, guaranteeing the animation fires.
 */
type Style = { [k: string]: string };

interface Carrier {
	key: string;
	style: Style;
	fading: boolean;
}

const CANVAS_SELECTOR = '.ad-preview-container .ad-preview, .ad-preview-container';

const PHASE1_MS = 320; // left edge travels to canvas.x (stretch)
const PHASE2_DELAY = 140; // gap between phase 1 end and phase 2 start
const PHASE2_MS = 280; // right edge + vertical snap to dest
const DWELL_MS = 160; // hold at dest before fade
const FADE_MS = 380; // preview cross-fade out
const BG_FADE_MS = 500; // background fade (starts when Phase 1 starts)
const POLL_MAX_MS = 2600;

@Component({
	selector: 'vml-agent-transition-overlay',
	standalone: true,
	imports: [CommonModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './agent-transition-overlay.component.html',
	styleUrls: ['./agent-transition-overlay.component.scss']
})
export class AgentTransitionOverlayComponent implements OnInit, OnDestroy {
	private readonly service = inject(AgentTransitionService);
	private readonly cdr = inject(ChangeDetectorRef);
	private sub?: Subscription;

	active = false;
	carriers: Carrier[] = [];

	private started = false;
	private pollRaf: number | null = null;
	private timers: ReturnType<typeof setTimeout>[] = [];

	ngOnInit(): void {
		this.sub = this.service.transition$.subscribe(s => this.apply(s));
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
		this.cleanup();
	}

	private apply(state: AgentTransitionState): void {
		if (!state.active) {
			if (this.active || this.started) this.reset();
			return;
		}
		if (this.started) return;

		const rects = this.service.peekHandoffRects();
		const src = rects['preview'];
		if (!src) return;

		this.started = true;
		this.active = true;

		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const mkClip = (t: number, r: number, b: number, l: number) => `inset(${t}px ${r}px ${b}px ${l}px round 10px)`;

		// Preview stretch carrier — full-screen host, clip-path masked to src rect.
		// No background carrier: we let the chat page's own .tx-exiting animations
		// run visibly, and the canvas is hidden via .tx-entering CSS on the editor
		// side so it doesn't flash before the carrier lands on it.
		const previewCarrier: Carrier = {
			key: 'preview',
			style: {
				left: '0',
				top: '0',
				width: '100%',
				height: '100%',
				'clip-path': mkClip(src.y, vw - src.x - src.width, vh - src.y - src.height, src.x),
				transition: 'none'
			},
			fading: false
		};

		this.carriers = [previewCarrier];
		this.cdr.markForCheck();

		// Start polling immediately for the canvas.
		requestAnimationFrame(() => {
			this.pollForCanvas(previewCarrier, src, vw, vh, mkClip);
		});
	}

	private pollForCanvas(
		carrier: Carrier,
		src: AgentTransitionRect,
		vw: number,
		vh: number,
		mkClip: (t: number, r: number, b: number, l: number) => string
	): void {
		const start = performance.now();

		const poll = () => {
			const el = document.querySelector(CANVAS_SELECTOR) as HTMLElement | null;
			const r = el?.getBoundingClientRect();

			if (r && r.width > 8 && r.height > 8) {
				this.runStretch(carrier, src, r, vw, vh, mkClip);
				return;
			}

			if (performance.now() - start > POLL_MAX_MS) {
				carrier.fading = true;
				this.cdr.markForCheck();
				this.timers.push(setTimeout(() => this.reset(), FADE_MS));
				return;
			}

			this.pollRaf = requestAnimationFrame(poll);
		};

		this.pollRaf = requestAnimationFrame(poll);
	}

	private runStretch(
		carrier: Carrier,
		src: AgentTransitionRect,
		dest: DOMRect,
		vw: number,
		vh: number,
		mkClip: (t: number, r: number, b: number, l: number) => string
	): void {
		const ease = 'cubic-bezier(0.22, 1, 0.32, 1)';

		// Phase 1 — left edge moves to dest.x.
		// Two-step: set `transition` first so the browser registers it before we
		// change `clip-path`, guaranteeing the animation fires.
		carrier.style = { ...carrier.style, transition: `clip-path ${PHASE1_MS}ms ${ease}` };
		this.cdr.markForCheck();

		this.timers.push(
			setTimeout(() => {
				carrier.style = {
					...carrier.style,
					'clip-path': mkClip(src.y, vw - src.x - src.width, vh - src.y - src.height, dest.x)
				};
				this.cdr.markForCheck();
			}, 0)
		);

		// Phase 2 — right edge + vertical snap to dest.
		const t2 = PHASE1_MS + PHASE2_DELAY;
		this.timers.push(
			setTimeout(() => {
				carrier.style = { ...carrier.style, transition: `clip-path ${PHASE2_MS}ms ${ease}` };
				this.cdr.markForCheck();

				this.timers.push(
					setTimeout(() => {
						carrier.style = {
							...carrier.style,
							'clip-path': mkClip(dest.y, vw - dest.x - dest.width, vh - dest.y - dest.height, dest.x)
						};
						this.cdr.markForCheck();
					}, 0)
				);
			}, t2)
		);

		// Fade out preview after it has landed.
		const tFade = t2 + PHASE2_MS + DWELL_MS;
		this.timers.push(
			setTimeout(() => {
				carrier.fading = true;
				this.cdr.markForCheck();
			}, tFade)
		);

		this.timers.push(setTimeout(() => this.reset(), tFade + FADE_MS));
	}

	private reset(): void {
		this.cleanup();
		this.active = false;
		this.carriers = [];
		this.started = false;
		this.cdr.markForCheck();
		this.service.cancel();
	}

	private cleanup(): void {
		if (this.pollRaf !== null) {
			cancelAnimationFrame(this.pollRaf);
			this.pollRaf = null;
		}
		this.timers.forEach(t => clearTimeout(t));
		this.timers = [];
	}
}
